/**
 * MODULE: domain/pages/frontend/components/PageCommentThreadGroup.tsx
 * PURPOSE: Root comment with nested replies and inline reply composer
 */

import { useState } from 'react';
import { Button } from 'flowbite-react';
import type { PageComment } from '../../contracts/pages.types';
import { CommentMentionInput } from './CommentMentionInput';
import { PageCommentThreadItem } from './PageCommentThreadItem';
import type { PageCommentThread } from '../utils/pageCommentThreadUtils';
import { isCommentResolved } from '../utils/blockCommentUtils';
import type { WorkspaceMember } from '@domain/workspace';

type Props = {
  thread: PageCommentThread;
  authorLabelFor: (userId: string) => string;
  workspaceId: string;
  pageId: string;
  members: WorkspaceMember[];
  currentUserId?: string;
  canComment: boolean;
  canResolve: boolean;
  canDeleteComment: (comment: PageComment) => boolean;
  onResolve: (commentId: string, resolved: boolean) => void;
  onDelete: (commentId: string) => void;
  onReply: (parentCommentId: string, content: string) => Promise<void>;
  replyPending: boolean;
  canCreateTask: boolean;
  taskIdForComment: (commentId: string) => string | null;
  onCreateTask: (comment: PageComment) => void;
  createTaskPendingFor: (commentId: string) => boolean;
  resolvePending: boolean;
  deletePending: boolean;
  compact?: boolean;
  testIdPrefix?: string;
  highlightedCommentId?: string | null;
};

export function PageCommentThreadGroup({
  thread,
  authorLabelFor,
  workspaceId,
  pageId,
  members,
  currentUserId,
  canComment,
  canResolve,
  canDeleteComment,
  onResolve,
  onDelete,
  onReply,
  replyPending,
  canCreateTask,
  taskIdForComment,
  onCreateTask,
  createTaskPendingFor,
  resolvePending,
  deletePending,
  compact = false,
  testIdPrefix = 'pages-comment',
  highlightedCommentId = null,
}: Props) {
  const { root, replies } = thread;
  const rootResolved = isCommentResolved(root);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');

  const handleReplySubmit = async () => {
    const content = replyDraft.trim();
    if (!content) {
      return;
    }
    await onReply(root.id, content);
    setReplyDraft('');
    setReplyOpen(false);
  };

  return (
    <li
      className="space-y-2"
      data-testid={`${testIdPrefix}-thread-${root.id}`}
      data-thread-resolved={rootResolved ? 'true' : 'false'}
    >
      <PageCommentThreadItem
        comment={root}
        authorLabel={authorLabelFor(root.created_by)}
        workspaceId={workspaceId}
        pageId={pageId}
        canResolve={canResolve}
        canDelete={canDeleteComment(root)}
        onResolve={(resolved) => onResolve(root.id, resolved)}
        onDelete={() => onDelete(root.id)}
        canCreateTask={canCreateTask}
        linkedTaskId={taskIdForComment(root.id)}
        onCreateTask={() => onCreateTask(root)}
        createTaskPending={createTaskPendingFor(root.id)}
        resolvePending={resolvePending}
        deletePending={deletePending}
        compact={compact}
        testIdPrefix={testIdPrefix}
        highlighted={root.id === highlightedCommentId}
        showReplyButton={canComment && !rootResolved}
        onReply={() => setReplyOpen((value) => !value)}
        replyOpen={replyOpen}
        replyCount={replies.length}
      />

      {replies.length > 0 ? (
        <ul
          className={`space-y-2 ${compact ? 'ml-3' : 'ml-4 border-l-2 border-gray-100 pl-3 dark:border-gray-800'}`}
          data-testid={`${testIdPrefix}-replies-${root.id}`}
        >
          {replies.map((reply) => (
            <PageCommentThreadItem
              key={reply.id}
              comment={reply}
              authorLabel={authorLabelFor(reply.created_by)}
              workspaceId={workspaceId}
              pageId={pageId}
              canResolve={false}
              canDelete={canDeleteComment(reply)}
              onResolve={() => undefined}
              onDelete={() => onDelete(reply.id)}
              canCreateTask={canCreateTask}
              linkedTaskId={taskIdForComment(reply.id)}
              onCreateTask={() => onCreateTask(reply)}
              createTaskPending={createTaskPendingFor(reply.id)}
              resolvePending={resolvePending}
              deletePending={deletePending}
              compact
              isReply
              testIdPrefix={testIdPrefix}
              highlighted={reply.id === highlightedCommentId}
            />
          ))}
        </ul>
      ) : null}

      {replyOpen && canComment && !rootResolved ? (
        <div
          className={`space-y-2 ${compact ? 'ml-3' : 'ml-4 border-l-2 border-blue-100 pl-3 dark:border-blue-900/40'}`}
          data-testid={`${testIdPrefix}-reply-compose-${root.id}`}
        >
          <CommentMentionInput
            value={replyDraft}
            onChange={setReplyDraft}
            members={members}
            currentUserId={currentUserId}
            disabled={replyPending}
            placeholder="Write a reply…"
            rows={compact ? 2 : 3}
            onSubmit={() => void handleReplySubmit()}
            submitDisabled={replyPending || !replyDraft.trim()}
          />
          <div className="flex items-center gap-2">
            <Button
              size={compact ? 'xs' : 'sm'}
              color="blue"
              onClick={() => void handleReplySubmit()}
              disabled={replyPending || !replyDraft.trim()}
              data-testid={`${testIdPrefix}-reply-submit-${root.id}`}
            >
              {replyPending ? 'Posting…' : 'Post reply'}
            </Button>
            <Button
              size={compact ? 'xs' : 'sm'}
              color="light"
              onClick={() => {
                setReplyOpen(false);
                setReplyDraft('');
              }}
              data-testid={`${testIdPrefix}-reply-cancel-${root.id}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
